import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AXIOS } from "hooks/axios";
import { useAuth } from "components/auth/AuthProvider";
import { useError } from "components/status/ErrorProvider";
import { useSuccess } from "components/status/SuccessProvider";

type PavmRequestConnectSchema = {
  uri: string;
  project: string;
  reason: string;
};

const useRequests = () => {
  const qc = useQueryClient();

  const refetch = () => {
    // get active, requests queries
    const queries = qc
      .getQueriesData({ type: "active" })
      .filter(([queryKey]) => {
        //@ts-ignore
        return queryKey[0].startsWith("USER SERVICE REQUESTS");
      });

    // refetch pending requests (since new request created)
    queries.forEach(([qKey]) => qc.refetchQueries(qKey));
  };

  return { refetch };
};

export const usePavmRequestConnect = () => {
  const { refetch } = useRequests();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    PavmRequestConnectSchema
  >(
    [`PAVM EMAIL REQUEST CONNECT`],
    ({ uri, project, reason }: PavmRequestConnectSchema) =>
      AXIOS.post(`${uri}/request/connect`, { project, reason }),
    {
      onSuccess: (data) => {
        setSuccess("Request submitted");

        // refetch pending requests (since new request created)
        refetch();

        return camelcaseKeys(data.data, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

type PavmRequestServiceRestartSchema = {
  host: string;
  reason: string;
  service: string;
  triggerAt: number;
};

export const usePavmRequestServiceRestart = () => {
  const { user } = useAuth();
  const { refetch } = useRequests();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const uri = user?.project?.uri;

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    PavmRequestServiceRestartSchema
  >(
    [`PAVM EMAIL REQUEST SERVICE RESTART`],
    (props: PavmRequestServiceRestartSchema) =>
      AXIOS.post(`${uri}/request/service/restart`, props),
    {
      onSuccess: (data) => {
        setSuccess("Request submitted");

        // refetch pending requests (since new request created)
        refetch();

        return camelcaseKeys(data.data, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

type PavmRequestFwupdateSchema = {
  ip: string;
  host: string;
  reason: string;
  fwupdate: {
    name: string;
    hash: string;
    size: number;
    triggerAt: number;
    lastModified: string;
  };
};

export const usePavmRequestFwupdate = () => {
  const { user } = useAuth();
  const { refetch } = useRequests();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const uri = user?.project?.uri;

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    PavmRequestFwupdateSchema
  >(
    [`PAVM EMAIL REQUEST FWUPDATE`],
    (props: PavmRequestFwupdateSchema) =>
      AXIOS.post(`${uri}/request/fwupdate`, props),
    {
      onSuccess: (data) => {
        setSuccess("Request submitted");

        // refetch pending requests (since new request created)
        refetch();

        return camelcaseKeys(data.data, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};
